import React from 'react';
import { Formik } from 'formik';

import { STATUSES } from 'constants/common';

import { getTranslation } from 'helpers/getTranslation';
import { getLessonStatusContextItems } from 'helpers/getLessonStatusContextItems';
import { getReadonlyLessonText } from 'helpers/getReadonlyLessonText';
import { isLessonSaveVisible } from 'helpers/isLessonSaveVisible';

import { Icon } from 'components/shared/Icon';
import { Button } from 'components/shared/Button';
import { Loading } from 'components/shared/Loading';

import { AutoSave } from 'components/AutoSave';
// import { CourseTree } from 'components/CourseTree';
import { LessonLayout } from 'components/Layout';
import { LessonHeader } from 'components/LessonHeader';
import { LessonLocaleTabs } from 'components/LessonLocaleTabs';
import LessonLocaleFormCompletionSync from 'components/LessonLocaleTabs/LessonLocaleFormCompletionSync';
import { ToolbarActions } from 'components/ToolbarActions';
import { ReadOnlySection } from 'components/ReadOnlySection';
import { BreadcrumbsToolbar } from 'components/Toolbar';

import { LessonsSection } from './components/LessonsSection';
import { MainSettings } from './components/MainSettings';
import { FigmaFileSettings } from './components/FigmaFileSettings';
import { ResyncSection } from './components/ResyncSection';
import { TasksSection } from './components/TasksSection';
import { GoodToKnowSection } from './components/GoodToKnowSection';
import { GoodToKnowSettings } from './components/GoodToKnowSettings';
import { KindSettings } from './components/KindSettings';
import { TaskByReferenceSettings } from './components/TaskByReferenceSettings';
import { TaskByDescriptionSettings } from './components/TaskByDescriptionSettings';
import { CanvasTheorySettings } from './components/CanvasTheorySettings';
import { CanvasTheorySection } from './components/CanvasTheorySection';
import { LessonGoals } from './components/LessonGoals';

import { convertDataToForm } from './helpers/convertDataToForm';
import { convertDataToApi } from './helpers/convertDataToApi';

import styles from './FigmaLessonPage.module.scss';

const FigmaLessonPage = ({
  loading,
  saveLoading,
  resyncLoading,
  continueLoading,
  lessonsOpen,
  readOnly,
  figmaFileImages,
  figmaFileMetadataLoading,
  figmaFileName,
  figmaFilePageName,
  hasFigmaToken,
  baseLesson,
  lesson,
  lastActionAt,
  currentStep,
  currentTask,
  courseTitle,
  courseStatus,
  isCatalogLesson,
  setCurrentStep,
  onAutoSaveSubmit,
  onTaskClick,
  onInitialStateClick,
  onTaskByDescriptionClick,
  onTaskByReferenceClick,
  onGoodToKnowClick,
  onCanvasTheoryClick,
  onContinueCreationClick,
  onSaveClick,
  onResyncClick,
  onPreviewClick,
  onRemoveClick,
  onLessonsCloseClick,
  onLessonsBtnClick,
  onStatusContextItemClick,
  activeLocale,
  availableLocales,
  localesWithContent,
  localeContentLoading,
  lessonType,
  onLocaleChange,
  onTaskTranslated,
  onFormTasksChange,
  translateAllTasksLoading,
  canTranslateAllTasksFromRu,
  onTranslateAllTasksFromRu,
}: {
  loading: any;
  saveLoading: any;
  resyncLoading: any;
  continueLoading: any;
  lessonsOpen: any;
  readOnly: any;
  figmaFileImages: any;
  figmaFileMetadataLoading: boolean;
  figmaFileName: string | null;
  figmaFilePageName: string | null;
  hasFigmaToken: boolean;
  baseLesson: any;
  lesson: any;
  lastActionAt: any;
  currentStep: any;
  currentTask: any;
  courseTitle: any;
  courseStatus: any;
  isCatalogLesson: any;
  setCurrentStep: any;
  onAutoSaveSubmit: any;
  onTaskClick: any;
  onInitialStateClick: any;
  onTaskByDescriptionClick: any;
  onTaskByReferenceClick: any;
  onGoodToKnowClick: any;
  onCanvasTheoryClick: any;
  onContinueCreationClick: any;
  onSaveClick: any;
  onResyncClick: any;
  onPreviewClick: any;
  onRemoveClick: any;
  onLessonsCloseClick: any;
  onLessonsBtnClick: any;
  onStatusContextItemClick: any;
  activeLocale: string;
  availableLocales: readonly string[];
  localesWithContent: string[];
  localeContentLoading: boolean;
  lessonType: string;
  onLocaleChange: (locale: string) => void;
  onTaskTranslated?: () => Promise<void>;
  onFormTasksChange?: (locale: string, tasks: unknown) => void;
  translateAllTasksLoading: boolean;
  canTranslateAllTasksFromRu: boolean;
  onTranslateAllTasksFromRu: () => Promise<void>;
}) => {
  const shellLesson = baseLesson;
  const formLesson = lesson || baseLesson;

  const formikKey = localeContentLoading
    ? `${shellLesson.id}-loading`
    : `${shellLesson.id}-${activeLocale}-ready`;

  return (
    <Formik
      key={formikKey}
      enableReinitialize
      validateOnMount
      initialValues={convertDataToForm(formLesson, readOnly)}
      onSubmit={onAutoSaveSubmit}
    >
    {({ values, setFieldValue }: { values: any; setFieldValue: any }) => (
      <>
        {onFormTasksChange && currentStep === 3 && (
          <LessonLocaleFormCompletionSync
            activeLocale={activeLocale}
            localeContentLoading={localeContentLoading}
            onFormTasksChange={onFormTasksChange}
          />
        )}
      <LessonLayout
        sidebarComponent={
          <div className={styles.sidebar}>
            {currentStep === 3 ? (
              <>
                <div className={styles.header}>
                  <Button
                    className={styles.lessonsBtn}
                    size="sm"
                    variant="link"
                    startIcon={<Icon name="lessonsTree" />}
                    onClick={onLessonsBtnClick}
                  >
                    {getTranslation('common.button.lessons')}
                  </Button>
                  <LessonsSection
                    open={lessonsOpen}
                    onClose={onLessonsCloseClick}
                  />
                  <ResyncSection
                    fileMetadataLoading={figmaFileMetadataLoading}
                    fileName={figmaFileName}
                    filePageName={figmaFilePageName}
                    haveToken={hasFigmaToken}
                    loading={resyncLoading}
                    readOnly={readOnly}
                    onResyncClick={() =>
                      onResyncClick(values, { setFieldValue })
                    }
                  />
                </div>
                <TasksSection
                  tasks={values.tasks}
                  currentTask={currentTask}
                  kind={shellLesson.kind}
                  designType={values.designType}
                  onTaskClick={onTaskClick}
                  onInitialStateClick={onInitialStateClick}
                  onTaskByReferenceClick={onTaskByReferenceClick}
                  onTaskByDescriptionClick={onTaskByDescriptionClick}
                />
                <GoodToKnowSection onClick={onGoodToKnowClick} />
                <CanvasTheorySection onClick={onCanvasTheoryClick} />
              </>
            ) : (
              // <CourseTree />
              <div />
            )}
          </div>
        }
        toolbarComponent={
          <BreadcrumbsToolbar
            breadcrumbsData={{ courseTitle, lessonTitle: values.title }}
            endAdornment={
              currentStep === 3 ? (
                <ToolbarActions
                  withRemove
                  withSave={isLessonSaveVisible({
                    status: shellLesson.status,
                    activeLocale,
                  })}
                  saveLoading={saveLoading}
                  contextItems={getLessonStatusContextItems({
                    courseReadOnly:
                      isCatalogLesson || courseStatus === STATUSES.PUBLISHED,
                    status: shellLesson.status,
                  })}
                  removeData={{ id: shellLesson.id, title: shellLesson.title }}
                  updatedAt={lastActionAt}
                  status={shellLesson.status}
                  onSaveClick={() => onSaveClick(values)}
                  onPreviewClick={onPreviewClick}
                  onRemoveClick={onRemoveClick}
                  onStatusContextItemClick={(contextItem: any) =>
                    onStatusContextItemClick(
                      contextItem,
                      convertDataToApi(values),
                    )
                  }
                />
              ) : (
                values.filePageId && (
                  <Button
                    className={styles.action}
                    size="md"
                    startIcon={
                      continueLoading && (
                        <Loading size={18} color="var(--color-white-primary)" />
                      )
                    }
                    endIcon={<Icon name="chevronRight" />}
                    onClick={() =>
                      onContinueCreationClick(values, { setFieldValue })
                    }
                  >
                    {getTranslation('common.button.continue_creation')}
                  </Button>
                )
              )
            }
          />
        }
      >
        <div className={styles.page}>
          {loading ? (
            <Loading />
          ) : (
            <>
              {!localeContentLoading && <AutoSave debounceMs={1000} />}
              {readOnly && (
                <ReadOnlySection
                  text={getReadonlyLessonText(shellLesson.status)}
                />
              )}

              {currentStep === 1 && (
                <>
                  <LessonHeader
                    readOnly={readOnly}
                    lessonRole={shellLesson.role}
                  />
                  <KindSettings setCurrentStep={setCurrentStep} />
                </>
              )}

              {currentStep === 2 && <FigmaFileSettings />}

              {currentStep === 3 && (
                <>
                  <LessonHeader
                    readOnly={readOnly}
                    lessonRole={shellLesson.role}
                  />
                  <div className={styles.localeRow}>
                    <LessonLocaleTabs
                      lessonId={shellLesson.id}
                      lessonType={lessonType}
                      activeLocale={activeLocale}
                      availableLocales={availableLocales}
                      localesWithContent={localesWithContent}
                      onLocaleChange={onLocaleChange}
                    />
                    {activeLocale === 'en' && (
                      <Button
                        className={styles.translateAllTasksBtn}
                        size="sm"
                        variant="outlined"
                        disabled={
                          !canTranslateAllTasksFromRu || translateAllTasksLoading
                        }
                        startIcon={
                          translateAllTasksLoading && (
                            <Loading size={18} color="var(--color-primary)" />
                          )
                        }
                        onClick={onTranslateAllTasksFromRu}
                      >
                        {getTranslation(
                          'figma_lesson_page.translate_all_tasks_from_ru',
                        )}
                      </Button>
                    )}
                  </div>
                  {localeContentLoading ? (
                    <Loading />
                  ) : (
                    <>
                      <LessonGoals readOnly={readOnly} />
                      {currentTask === 'BY_TASK_DESCRIPTION' && (
                        <TaskByDescriptionSettings />
                      )}
                      {currentTask !== 'GOOD_TO_KNOW' &&
                        currentTask !== 'CANVAS_THEORY' &&
                        currentTask !== 'BY_REFERENCE' && (
                          <MainSettings
                            loading={loading}
                            figmaFileImages={figmaFileImages}
                            currentTask={
                              currentTask === 'BY_TASK_DESCRIPTION'
                                ? 0
                                : currentTask
                            }
                            designType={values.designType}
                            activeLocale={activeLocale}
                            lessonType={lessonType}
                            onTaskTranslated={onTaskTranslated}
                          />
                        )}
                      {currentTask === 'BY_REFERENCE' && (
                        <TaskByReferenceSettings />
                      )}
                      {currentTask === 'GOOD_TO_KNOW' && (
                        <GoodToKnowSettings />
                      )}
                      {currentTask === 'CANVAS_THEORY' && (
                        <CanvasTheorySettings />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </LessonLayout>
      </>
    )}
  </Formik>
  );
};

export default React.memo(FigmaLessonPage) as any;
